<!-- SkyPlus Case Detail -->
<div class="sp-dashboard-container">
  <sp-header></sp-header>

  <main class="sp-main">

    <!-- Loading -->
    <div class="sp-loading" *ngIf="loading">
      <span class="material-icons spinning">autorenew</span>
      <p>Loading case…</p>
    </div>

    <!-- Error -->
    <div class="sp-error" *ngIf="error">
      <span class="material-icons">error_outline</span>
      <h2>{{ error }}</h2>
      <button class="btn btn-secondary" (click)="backToList()">Back to cases</button>
    </div>

    <!-- Case detail -->
    <ng-container *ngIf="!loading && !error && case_">

      <!-- Page header / breadcrumb -->
      <div class="sp-page-header">
        <div class="sp-greeting">
          <div class="breadcrumb">
            <a (click)="backToDashboard()" class="crumb">Dashboard</a>
            <span class="crumb-sep">/</span>
            <a (click)="backToList()" class="crumb">All Cases</a>
            <span class="crumb-sep">/</span>
            <span class="crumb crumb-active">{{ case_.id }}</span>
          </div>
        </div>
        <div class="sp-header-actions">
          <button class="btn btn-secondary" (click)="exportCase()">
            <span class="material-icons">picture_as_pdf</span> Export Bundle
          </button>
          <button class="btn btn-secondary" (click)="editCase()">
            <span class="material-icons">edit</span> Edit
          </button>
          <button class="btn btn-primary"
                  (click)="closeCase()"
                  *ngIf="case_.status !== 'CLOSED'">
            <span class="material-icons">check_circle</span> Close Case
          </button>
        </div>
      </div>

      <!-- Case summary card -->
      <section class="case-summary">
        <div class="summary-top">
          <div class="summary-title">
            <div class="title-row">
              <span class="priority-flag"
                    [style.background-color]="PRIORITY_COLORS[case_.priority]"
                    [title]="case_.priority + ' priority'">
                {{ case_.priority.charAt(0) }}
              </span>
              <span class="case-id-label">{{ case_.id }}</span>
              <span class="status-pill" [style.background-color]="STATUS_COLORS[case_.status]">
                {{ STATUS_LABELS[case_.status] }}
              </span>
              <span class="type-chip">{{ TYPE_LABELS[case_.type] }}</span>
              <span class="role-chip" [class.role-plaintiff]="case_.ourRole === 'PLAINTIFF'"
                                        [class.role-defendant]="case_.ourRole === 'DEFENDANT'">
                We are {{ case_.ourRole === 'PLAINTIFF' ? 'PLAINTIFF' : 'DEFENDANT' }}
              </span>
            </div>
            <h2>{{ case_.title }}</h2>
            <div class="source-ref" *ngIf="case_.sourceRef">
              <span class="material-icons">link</span>
              Source: <strong>{{ case_.sourceRef }}</strong>
              <span class="source-desc" *ngIf="case_.sourceDescription"> — {{ case_.sourceDescription }}</span>
            </div>
          </div>

          <div class="summary-exposure">
            <div class="exposure-label">Exposure</div>
            <div class="exposure-value">{{ formatCurrency(case_.exposureAmount) }}</div>
            <div class="recovered-line" *ngIf="case_.recoveredAmount && case_.recoveredAmount > 0">
              <span class="material-icons">savings</span>
              {{ formatCurrency(case_.recoveredAmount) }} recovered
            </div>
          </div>
        </div>

        <div class="summary-grid">
          <div class="summary-field">
            <div class="field-label">Opposing Party</div>
            <div class="field-value">{{ case_.opposingParty }}</div>
            <div class="field-sub" *ngIf="case_.opposingCounsel">
              <span class="material-icons">work</span>{{ case_.opposingCounsel }}
            </div>
          </div>
          <div class="summary-field">
            <div class="field-label">Assigned To</div>
            <div class="field-value">{{ case_.assignedTo }}</div>
            <div class="field-sub" *ngIf="case_.assignedDepartment">{{ case_.assignedDepartment }}</div>
          </div>
          <div class="summary-field" *ngIf="case_.externalCounsel">
            <div class="field-label">External Counsel</div>
            <div class="field-value">{{ case_.externalCounsel }}</div>
          </div>
          <div class="summary-field">
            <div class="field-label">Court</div>
            <div class="field-value">{{ case_.court || '—' }}</div>
            <div class="field-sub" *ngIf="case_.courtCaseNumber">{{ case_.courtCaseNumber }}</div>
          </div>
          <div class="summary-field" *ngIf="case_.judge">
            <div class="field-label">Judicial Officer</div>
            <div class="field-value">{{ case_.judge }}</div>
          </div>
          <div class="summary-field">
            <div class="field-label">Opened</div>
            <div class="field-value">{{ formatDate(case_.openedDate) }}</div>
          </div>
          <div class="summary-field" *ngIf="case_.expectedCloseDate">
            <div class="field-label">Expected Close</div>
            <div class="field-value">{{ formatDate(case_.expectedCloseDate) }}</div>
          </div>
          <div class="summary-field" *ngIf="case_.nextEventDate">
            <div class="field-label">Next Event</div>
            <div class="field-value" [class]="urgencyClass(case_.nextEventDate)">
              {{ formatDate(case_.nextEventDate) }}
            </div>
            <div class="field-sub" [class]="urgencyClass(case_.nextEventDate)">
              <ng-container *ngIf="daysUntil(case_.nextEventDate)! < 0">{{ -daysUntil(case_.nextEventDate)! }} days overdue</ng-container>
              <ng-container *ngIf="daysUntil(case_.nextEventDate)! === 0">Today</ng-container>
              <ng-container *ngIf="daysUntil(case_.nextEventDate)! > 0">in {{ daysUntil(case_.nextEventDate) }} days</ng-container>
            </div>
          </div>
          <div class="summary-field" *ngIf="case_.status === 'CLOSED' && case_.outcome">
            <div class="field-label">Outcome</div>
            <div class="field-value outcome-{{ case_.outcome.toLowerCase() }}">
              <span class="material-icons">{{ case_.outcome === 'WON' || case_.outcome === 'SETTLED' ? 'check_circle' : 'cancel' }}</span>
              {{ case_.outcome }}
            </div>
            <div class="field-sub" *ngIf="case_.outcomeNote">{{ case_.outcomeNote }}</div>
          </div>
        </div>

        <!-- Tags -->
        <div class="summary-tags" *ngIf="case_.tags && case_.tags.length > 0">
          <span class="tag" *ngFor="let t of case_.tags">#{{ t }}</span>
        </div>
      </section>

      <!-- Tabs nav -->
      <nav class="tabs-nav">
        <button class="tab" [class.active]="activeTab === 'overview'"   (click)="switchTab('overview')">
          <span class="material-icons">dashboard</span> Overview
        </button>
        <button class="tab" [class.active]="activeTab === 'parties'"    (click)="switchTab('parties')">
          <span class="material-icons">group</span> Parties
          <span class="tab-badge">{{ parties.length }}</span>
        </button>
        <button class="tab" [class.active]="activeTab === 'documents'"  (click)="switchTab('documents')">
          <span class="material-icons">folder_shared</span> Documents
          <span class="tab-badge">{{ documents.length }}</span>
        </button>
        <button class="tab" [class.active]="activeTab === 'events'"     (click)="switchTab('events')">
          <span class="material-icons">event</span> Events
          <span class="tab-badge highlight" *ngIf="upcomingEventCount > 0">{{ upcomingEventCount }}</span>
        </button>
        <button class="tab" [class.active]="activeTab === 'tasks'"      (click)="switchTab('tasks')">
          <span class="material-icons">task_alt</span> Tasks
          <span class="tab-badge alert" *ngIf="overdueTaskCount > 0">{{ overdueTaskCount }}</span>
          <span class="tab-badge" *ngIf="overdueTaskCount === 0 && openTaskCount > 0">{{ openTaskCount }}</span>
        </button>
        <button class="tab" [class.active]="activeTab === 'activity'"   (click)="switchTab('activity')">
          <span class="material-icons">history</span> Activity
        </button>
        <button class="tab" [class.active]="activeTab === 'costs'"      (click)="switchTab('costs')">
          <span class="material-icons">payments</span> Costs &amp; Recovery
        </button>
        <button class="tab" [class.active]="activeTab === 'notes'"      (click)="switchTab('notes')">
          <span class="material-icons">sticky_note_2</span> Notes
          <span class="tab-badge" *ngIf="notes.length > 0">{{ notes.length }}</span>
        </button>
      </nav>

      <!-- Tab content -->
      <section class="tab-content">

        <!-- ─── Overview ─── -->
        <div *ngIf="activeTab === 'overview'" class="overview-grid">
          <div class="overview-card">
            <div class="oc-header">
              <span class="material-icons">event</span>
              <h3>Next Up</h3>
            </div>
            <div class="oc-body">
              <ng-container *ngIf="case_.nextEventDate && events.length > 0; else noNext">
                <div class="next-event-box" [class]="urgencyClass(case_.nextEventDate)">
                  <div class="next-event-pill">
                    <div class="next-event-day">{{ case_.nextEventDate | date:'d' }}</div>
                    <div class="next-event-month">{{ case_.nextEventDate | date:'MMM' }}</div>
                  </div>
                  <div class="next-event-info">
                    <div class="next-event-title">{{ events[0].title }}</div>
                    <div class="next-event-meta">{{ events[0].location }}</div>
                    <div class="next-event-when">
                      <ng-container *ngIf="daysUntil(case_.nextEventDate)! < 0">{{ -daysUntil(case_.nextEventDate)! }} days OVERDUE</ng-container>
                      <ng-container *ngIf="daysUntil(case_.nextEventDate)! === 0">Today, {{ case_.nextEventDate | date:'HH:mm' }}</ng-container>
                      <ng-container *ngIf="daysUntil(case_.nextEventDate)! > 0">in {{ daysUntil(case_.nextEventDate) }} days</ng-container>
                    </div>
                  </div>
                </div>
                <button class="link-btn" (click)="switchTab('events')">View all events →</button>
              </ng-container>
              <ng-template #noNext>
                <div class="empty">No events scheduled.</div>
              </ng-template>
            </div>
          </div>

          <div class="overview-card">
            <div class="oc-header">
              <span class="material-icons">task_alt</span>
              <h3>Tasks</h3>
            </div>
            <div class="oc-body">
              <div class="oc-stat-row">
                <div class="oc-stat"><div class="n">{{ openTaskCount }}</div><div class="lbl">Open</div></div>
                <div class="oc-stat alert"><div class="n">{{ overdueTaskCount }}</div><div class="lbl">Overdue</div></div>
                <div class="oc-stat"><div class="n">{{ tasks.length - openTaskCount }}</div><div class="lbl">Done</div></div>
              </div>
              <button class="link-btn" (click)="switchTab('tasks')">Manage tasks →</button>
            </div>
          </div>

          <div class="overview-card">
            <div class="oc-header">
              <span class="material-icons">payments</span>
              <h3>Financial Position</h3>
            </div>
            <div class="oc-body">
              <div class="finance-row">
                <span class="finance-label">Costs incurred</span>
                <span class="finance-amount neg">{{ formatCurrency(totalCosts) }}</span>
              </div>
              <div class="finance-row">
                <span class="finance-label">Amount recovered</span>
                <span class="finance-amount pos">{{ formatCurrency(totalRecovered) }}</span>
              </div>
              <div class="finance-row total">
                <span class="finance-label">Net position</span>
                <span class="finance-amount" [class.pos]="netPosition >= 0" [class.neg]="netPosition < 0">
                  {{ formatCurrency(netPosition) }}
                </span>
              </div>
              <button class="link-btn" (click)="switchTab('costs')">View ledger →</button>
            </div>
          </div>

          <div class="overview-card wide">
            <div class="oc-header">
              <span class="material-icons">history</span>
              <h3>Recent Activity</h3>
            </div>
            <div class="oc-body">
              <div class="activity-mini">
                <div class="activity-mini-row" *ngFor="let a of activity.slice(0, 5)">
                  <span class="material-icons">{{ activityIcon(a.category) }}</span>
                  <div class="ami-content">
                    <span><strong>{{ a.actor }}</strong> {{ a.action }}<span *ngIf="a.details"> &middot; {{ a.details }}</span></span>
                    <small>{{ formatTimeAgo(a.timestamp) }}</small>
                  </div>
                </div>
              </div>
              <button class="link-btn" (click)="switchTab('activity')">Full audit trail →</button>
            </div>
          </div>
        </div>

        <!-- ─── Parties ─── -->
        <div *ngIf="activeTab === 'parties'" class="parties-tab">
          <div class="tab-toolbar">
            <h3>{{ parties.length }} parties</h3>
            <sp-case-add [caseRef]="caseId" kind="party" (added)="reloadParties()"></sp-case-add>
          </div>
          <div class="parties-grid">
            <div class="party-card" *ngFor="let p of parties" [class]="partyRoleClass(p.role)">
              <div class="party-header">
                <div class="party-avatar">{{ p.name.charAt(0) }}</div>
                <div class="party-name-block">
                  <div class="party-name">{{ p.name }}</div>
                  <div class="party-role">{{ partyRoleLabel(p.role) }}</div>
                </div>
                <span class="party-type">{{ p.type }}</span>
              </div>
              <div class="party-body">
                <div class="pf" *ngIf="p.idNumber"><span class="material-icons">badge</span>{{ p.idNumber }}</div>
                <div class="pf" *ngIf="p.contact"><span class="material-icons">call</span>{{ p.contact }}</div>
                <div class="pf" *ngIf="p.email"><span class="material-icons">mail</span>{{ p.email }}</div>
                <div class="pf" *ngIf="p.address"><span class="material-icons">place</span>{{ p.address }}</div>
                <div class="pf" *ngIf="p.representedBy"><span class="material-icons">work</span>{{ p.representedBy }}</div>
                <div class="party-notes" *ngIf="p.notes">{{ p.notes }}</div>
              </div>
            </div>
            <div class="empty-tab" *ngIf="parties.length === 0">
              <span class="material-icons">group_off</span>
              <p>No parties added yet.</p>
            </div>
          </div>
        </div>

        <!-- ─── Documents ─── -->
        <div *ngIf="activeTab === 'documents'" class="documents-tab">
          <div class="tab-toolbar">
            <h3>{{ documents.length }} documents</h3>
            <sp-case-doc-actions [caseRef]="caseId" (changed)="reloadDocuments()"></sp-case-doc-actions>
          </div>
          <table class="doc-table">
            <thead>
              <tr>
                <th class="dc-icon"></th>
                <th>Document</th>
                <th>Type</th>
                <th>Uploaded By</th>
                <th>Date</th>
                <th class="right">Size</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let d of documents">
                <td class="dc-icon">
                  <span class="material-icons doc-icon">{{ DOC_ICONS[d.type] }}</span>
                </td>
                <td class="doc-name">{{ d.name }}</td>
                <td><span class="type-pill">{{ DOC_LABELS[d.type] }}</span></td>
                <td>{{ d.uploadedBy }}</td>
                <td>{{ formatDate(d.uploadedAt) }}</td>
                <td class="right">{{ formatBytes(d.size) }}</td>
              </tr>
              <tr *ngIf="documents.length === 0">
                <td colspan="6" class="empty-cell">No documents uploaded yet. Click "Upload Document" to add one.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- ─── Events ─── -->
        <div *ngIf="activeTab === 'events'" class="events-tab">
          <div class="tab-toolbar">
            <h3>{{ events.length }} events scheduled</h3>
            <sp-case-add [caseRef]="caseId" kind="event" (added)="reloadEvents()"></sp-case-add>
          </div>
          <div class="event-list">
            <div class="event-card"
                 *ngFor="let e of events"
                 [class]="eventStatusClass(e.status)">
              <div class="event-date-block">
                <div class="event-d">{{ e.scheduledAt | date:'d' }}</div>
                <div class="event-m">{{ e.scheduledAt | date:'MMM' }}</div>
                <div class="event-y">{{ e.scheduledAt | date:'yyyy' }}</div>
              </div>
              <div class="event-info">
                <div class="event-info-top">
                  <span class="event-type-chip">
                    <span class="material-icons">{{ EVENT_ICONS[e.type] }}</span>
                    {{ EVENT_LABELS[e.type] }}
                  </span>
                  <span class="event-status-chip status-{{ e.status.toLowerCase() }}">{{ e.status }}</span>
                </div>
                <h4>{{ e.title }}</h4>
                <div class="event-attrs">
                  <span><span class="material-icons">schedule</span>{{ e.scheduledAt | date:'EEE, HH:mm' }} &middot; {{ e.durationMinutes }} min</span>
                  <span><span class="material-icons">place</span>{{ e.location }}</span>
                  <span *ngIf="e.attendees.length > 0">
                    <span class="material-icons">group</span>{{ e.attendees.join(', ') }}
                  </span>
                </div>
                <div class="event-outcome" *ngIf="e.outcomeNote">
                  <strong>Outcome:</strong> {{ e.outcomeNote }}
                </div>
                <div class="event-reminders" *ngIf="e.reminderDaysBefore.length > 0 && e.status === 'UPCOMING'">
                  <span class="material-icons">notifications_active</span>
                  Reminders set: {{ e.reminderDaysBefore.join('d, ') }}d before
                </div>
              </div>
            </div>
            <div class="empty-tab" *ngIf="events.length === 0">
              <span class="material-icons">event_busy</span>
              <p>No events scheduled.</p>
            </div>
          </div>
        </div>

        <!-- ─── Tasks ─── -->
        <div *ngIf="activeTab === 'tasks'" class="tasks-tab">
          <div class="tab-toolbar">
            <h3>{{ tasks.length }} tasks ({{ openTaskCount }} open, {{ overdueTaskCount }} overdue)</h3>
            <sp-case-add [caseRef]="caseId" kind="task" (added)="reloadTasks()"></sp-case-add>
          </div>
          <div class="task-list">
            <div class="task-row"
                 *ngFor="let t of tasks"
                 [class]="taskStatusClass(t.status)">
              <span class="material-icons task-icon"
                    [class.checked]="t.status === 'DONE'">
                {{ t.status === 'DONE' ? 'check_box' : 'check_box_outline_blank' }}
              </span>
              <div class="task-body">
                <div class="task-desc" [class.done]="t.status === 'DONE'">{{ t.description }}</div>
                <div class="task-meta">
                  <span><span class="material-icons">person</span>{{ t.assignee }}</span>
                  <span class="due-date" [class]="urgencyClass(t.dueDate)">
                    <span class="material-icons">schedule</span>
                    Due {{ formatDate(t.dueDate) }}
                  </span>
                  <span class="task-status-chip status-{{ t.status.toLowerCase() }}">{{ t.status }}</span>
                </div>
              </div>
            </div>
            <div class="empty-tab" *ngIf="tasks.length === 0">
              <span class="material-icons">task</span>
              <p>No tasks for this case yet.</p>
            </div>
          </div>
        </div>

        <!-- ─── Activity ─── -->
        <div *ngIf="activeTab === 'activity'" class="activity-tab">
          <div class="tab-toolbar"><h3>{{ activity.length }} entries</h3></div>
          <div class="activity-timeline">
            <div class="atimeline-item" *ngFor="let a of activity">
              <div class="atimeline-dot" [class]="'act-' + a.category.toLowerCase()">
                <span class="material-icons">{{ activityIcon(a.category) }}</span>
              </div>
              <div class="atimeline-content">
                <div class="atimeline-action">
                  <strong>{{ a.actor }}</strong> {{ a.action }}
                </div>
                <div class="atimeline-details" *ngIf="a.details">{{ a.details }}</div>
                <div class="atimeline-time">{{ formatDateTime(a.timestamp) }} &middot; {{ formatTimeAgo(a.timestamp) }}</div>
              </div>
            </div>
            <div class="empty-tab" *ngIf="activity.length === 0">
              <span class="material-icons">history_toggle_off</span>
              <p>No activity logged.</p>
            </div>
          </div>
        </div>

        <!-- ─── Costs ─── -->
        <div *ngIf="activeTab === 'costs'" class="costs-tab">
          <div class="tab-toolbar">
            <h3>Cost &amp; Recovery Ledger</h3>
            <sp-case-add [caseRef]="caseId" kind="cost" (added)="reloadCosts()"></sp-case-add>
          </div>
          <div class="cost-summary">
            <div class="cost-tile">
              <div class="cost-tile-label">Total costs</div>
              <div class="cost-tile-value neg">{{ formatCurrency(totalCosts) }}</div>
            </div>
            <div class="cost-tile">
              <div class="cost-tile-label">Total recovered</div>
              <div class="cost-tile-value pos">{{ formatCurrency(totalRecovered) }}</div>
            </div>
            <div class="cost-tile">
              <div class="cost-tile-label">Net position</div>
              <div class="cost-tile-value" [class.pos]="netPosition >= 0" [class.neg]="netPosition < 0">
                {{ formatCurrency(netPosition) }}
              </div>
            </div>
          </div>
          <table class="cost-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Type</th>
                <th>Paid To / Received From</th>
                <th>Reference</th>
                <th class="right">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let c of costs">
                <td>{{ formatDate(c.date) }}</td>
                <td>{{ c.description }}</td>
                <td><span class="type-pill">{{ c.type }}</span></td>
                <td>{{ c.paidTo || '—' }}</td>
                <td><span class="ref">{{ c.reference || '—' }}</span></td>
                <td class="right" [class.neg]="c.amount > 0" [class.pos]="c.amount < 0">
                  {{ c.amount < 0 ? '+' : '' }}{{ formatCurrency(Math.abs(c.amount)) }}
                </td>
              </tr>
              <tr *ngIf="costs.length === 0">
                <td colspan="6" class="empty-cell">No cost entries yet.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- ─── Notes ─── -->
        <div *ngIf="activeTab === 'notes'" class="notes-tab">
          <div class="tab-toolbar">
            <h3>{{ notes.length }} notes</h3>
            <sp-case-add [caseRef]="caseId" kind="note" (added)="reloadNotes()"></sp-case-add>
          </div>
          <div class="notes-list">
            <div class="note-card" *ngFor="let n of notes" [class.privileged]="n.isPrivileged">
              <div class="note-header">
                <div class="note-author-block">
                  <div class="note-avatar">{{ n.author.charAt(0) }}</div>
                  <div>
                    <div class="note-author">{{ n.author }}</div>
                    <div class="note-time">{{ formatDateTime(n.createdAt) }}</div>
                  </div>
                </div>
                <span class="priv-tag" *ngIf="n.isPrivileged">
                  <span class="material-icons">lock</span>
                  Privileged
                </span>
              </div>
              <div class="note-content">{{ n.content }}</div>
            </div>
            <div class="empty-tab" *ngIf="notes.length === 0">
              <span class="material-icons">note_add</span>
              <p>No notes yet — click "Add Note" to record one.</p>
            </div>
          </div>
        </div>
      </section>
    </ng-container>
  </main>
</div>
